/**
 * First-party identity consent signal (plan D15 as revised 2026-09-03,
 * owner decision 3). The page never decides the consent *state*; it reports a
 * normalized signal the auth host turns into a decision with the visitor's
 * geo:
 *
 *   approved        TCF purpose 1 granted / GPP applicable with no opt-out
 *   denied          first-party opt-out, TCF purpose 1 refused, GPP opt-out
 *   undecided       CMP present but the visitor has not chosen yet
 *   not_applicable  no CMP framework applies (Canada and most other markets)
 *
 * Only TCF purpose 1 (store/access information on a device) and the GPP
 * sale/sharing/targeting opt-outs are read for the identity signal.
 *
 * Advertising purpose (plans/ads-identity-unification.md, 2026-09-09): a second
 * normalized signal `adsSignal` rides with every bootstrap — TCF purposes 1, 3
 * and 4 granted → `approved`; the GPP sale / sharing / targeted-advertising
 * opt-outs → `denied`; the first-party ads opt-out marker → `denied`. The two
 * signals are decided by the auth host separately; neither implies the other.
 * @module lib/identity/consent
 */

const OPT_OUT_STORAGE_KEY = '__milla_identity_optout__';
const ADS_OPT_OUT_STORAGE_KEY = '__milla_ads_optout__';
const TCF_ADS_PURPOSES = [1, 3, 4];
const DEFAULT_TIMEOUT_MS = 350;

function readFirstPartyOptOut() {
  try {
    return typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem(OPT_OUT_STORAGE_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function writeFirstPartyOptOut(optedOut) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (optedOut) window.localStorage.setItem(OPT_OUT_STORAGE_KEY, '1');
    else window.localStorage.removeItem(OPT_OUT_STORAGE_KEY);
  } catch (_) {
    // storage unavailable: the auth host keeps the opt-out cookie anyway
  }
}

function readAdsOptOut() {
  try {
    return typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem(ADS_OPT_OUT_STORAGE_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function writeAdsOptOut(optedOut) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (optedOut) window.localStorage.setItem(ADS_OPT_OUT_STORAGE_KEY, '1');
    else window.localStorage.removeItem(ADS_OPT_OUT_STORAGE_KEY);
  } catch (_) {
    // storage unavailable: the auth host keeps the ads opt-out cookie anyway
  }
}

function withTimeout(promise, timeoutMs, fallback) {
  return new Promise((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(fallback);
    }, timeoutMs);
    promise.then((value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    }, () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(fallback);
    });
  });
}

function tcfSignal(tcData) {
  if (tcData.gdprApplies === false) return { signal: 'not_applicable', adsSignal: 'not_applicable', regime: 'tcf' };
  const decided = tcData.eventStatus === 'tcloaded' || tcData.eventStatus === 'useractioncomplete';
  if (!decided) return { signal: 'undecided', adsSignal: 'undecided', regime: 'tcf' };
  const consents = (tcData.purpose && tcData.purpose.consents) || {};
  const purposeOne = consents[1] === true;
  const adsPurposes = TCF_ADS_PURPOSES.every((purpose) => consents[purpose] === true);
  return { signal: purposeOne ? 'approved' : 'denied', adsSignal: adsPurposes ? 'approved' : 'denied', regime: 'tcf' };
}

// TCF 2.2 reads through `addEventListener`: Google's CMP answers the deprecated
// `getTCData` unsuccessfully (2026-09-28 EEA check). The first callback carries
// the current state, then the listener is removed.
function readTcf(win) {
  return new Promise((resolve, reject) => {
    try {
      let answered = false;
      win.__tcfapi('addEventListener', 2, (tcData, success) => {
        if (answered) return;
        answered = true;
        if (tcData && tcData.listenerId !== undefined && tcData.listenerId !== null) {
          try { win.__tcfapi('removeEventListener', 2, () => {}, tcData.listenerId); } catch (_) { /* the CMP keeps its own listener list */ }
        }
        if (!success || !tcData) {
          reject(new Error('tcf_unavailable'));
          return;
        }
        resolve(tcfSignal(tcData));
      });
    } catch (error) {
      reject(error);
    }
  });
}

const GPP_OPT_OUT_FIELDS = ['SaleOptOut', 'SharingOptOut', 'TargetedAdvertisingOptOut'];

function gppSignal(ping) {
  const sections = Array.isArray(ping.applicableSections) ? ping.applicableSections.filter((section) => Number.isInteger(section) && section > 0) : [];
  if (!sections.length) return { signal: 'not_applicable', adsSignal: 'not_applicable', regime: 'gpp' };
  if (ping.cmpStatus && ping.cmpStatus !== 'loaded') return { signal: 'undecided', adsSignal: 'undecided', regime: 'gpp' };
  const parsed = ping.parsedSections && typeof ping.parsedSections === 'object' ? ping.parsedSections : null;
  let optedOut = false;
  if (parsed) {
    Object.keys(parsed).forEach((key) => {
      // GPP 1.1 may list a section as an array of sub-sections.
      [].concat(parsed[key]).forEach((section) => {
        if (!section || typeof section !== 'object') return;
        GPP_OPT_OUT_FIELDS.forEach((field) => {
          if (section[field] === 1) optedOut = true;
        });
      });
    });
  }
  const signal = optedOut ? 'denied' : 'approved';
  return { signal, adsSignal: signal, regime: 'gpp' };
}

function readGpp(win) {
  return new Promise((resolve, reject) => {
    try {
      win.__gpp('ping', (ping, success) => {
        if (success === false || !ping) {
          reject(new Error('gpp_unavailable'));
          return;
        }
        resolve(gppSignal(ping));
      });
    } catch (error) {
      reject(error);
    }
  });
}

// Google's Privacy & messaging CMP reaches the page through GPT, which the SDK
// loads only after the identity bootstrap — so on EEA/US pages the first
// bootstrap never sees it (2026-09-28 check). These helpers let the identity
// module wait for the CMP and follow its later decisions.
function hasConsentApi() {
  return typeof window !== 'undefined' && (typeof window.__tcfapi === 'function' || typeof window.__gpp === 'function');
}

function waitForConsentApi({ timeoutMs = 8000, intervalMs = 100 } = {}) {
  return new Promise((resolve) => {
    if (hasConsentApi()) { resolve(true); return; }
    const started = Date.now();
    const timer = setInterval(() => {
      if (hasConsentApi()) { clearInterval(timer); resolve(true); }
      else if (Date.now() - started >= timeoutMs) { clearInterval(timer); resolve(false); }
    }, intervalMs);
    if (timer && typeof timer.unref === 'function') timer.unref();
  });
}

/**
 * Calls `onChange()` whenever the page's CMP reports a (possibly) new
 * decision — TCF events or GPP events. The caller re-reads the signal and
 * compares. Returns an unsubscribe function; a no-op without a CMP.
 */
function watchConsentApi(onChange) {
  if (typeof window === 'undefined') return () => {};
  const win = window;
  // Both APIs can be present at once (Google's CMP); follow each of them.
  let tcfListenerId = null;
  let gppListenerId = null;
  const notify = () => { try { onChange(); } catch (_) { /* a listener never breaks the CMP */ } };
  try {
    if (typeof win.__tcfapi === 'function') {
      win.__tcfapi('addEventListener', 2, (tcData, success) => {
        if (tcData && tcData.listenerId !== undefined && tcData.listenerId !== null) tcfListenerId = tcData.listenerId;
        if (success && tcData && (tcData.eventStatus === 'useractioncomplete' || tcData.eventStatus === 'tcloaded')) notify();
      });
    }
  } catch (_) { /* a broken TCF API leaves GPP */ }
  try {
    if (typeof win.__gpp === 'function') {
      win.__gpp('addEventListener', (event, success) => {
        if (event && event.listenerId !== undefined && event.listenerId !== null) gppListenerId = event.listenerId;
        if (success !== false && event) notify();
      });
    }
  } catch (_) { /* a broken GPP API leaves TCF */ }
  return () => {
    try { if (tcfListenerId !== null && typeof win.__tcfapi === 'function') win.__tcfapi('removeEventListener', 2, () => {}, tcfListenerId); } catch (_) { /* nothing to undo */ }
    try { if (gppListenerId !== null && typeof win.__gpp === 'function') win.__gpp('removeEventListener', () => {}, gppListenerId); } catch (_) { /* nothing to undo */ }
  };
}

/**
 * Resolves the normalized signal within `timeoutMs`. A CMP that does not
 * answer in time yields `undecided` (never `approved`).
 */
async function readConsentSignal({ timeoutMs = DEFAULT_TIMEOUT_MS } = {}) {
  const identityOptOut = readFirstPartyOptOut();
  const adsOptOut = readAdsOptOut();
  // Reading opt-out cannot bypass the CMP's separate advertising decision.
  // Resolve the framework first, then apply each first-party purpose override.
  const withOptOuts = (resolved) => ({
    ...resolved,
    ...(identityOptOut ? { signal: 'denied', regime: 'first_party' } : {}),
    ...(adsOptOut ? { adsSignal: 'denied' } : {}),
  });
  if (typeof window === 'undefined') return withOptOuts({ signal: 'not_applicable', adsSignal: 'not_applicable', regime: 'none' });
  if (typeof window.__tcfapi === 'function') {
    const tcf = await withTimeout(readTcf(window), timeoutMs, { signal: 'undecided', adsSignal: 'undecided', regime: 'tcf' });
    // Google's CMP installs both APIs everywhere (2026-09-28 regional check):
    // outside Europe TCF answers gdprApplies false and the GPP sections carry
    // the US choices, so a not-applicable TCF answer defers to GPP.
    if (tcf.signal !== 'not_applicable' || typeof window.__gpp !== 'function') return withOptOuts(tcf);
  }
  if (typeof window.__gpp === 'function') {
    return withOptOuts(await withTimeout(readGpp(window), timeoutMs, { signal: 'undecided', adsSignal: 'undecided', regime: 'gpp' }));
  }
  return withOptOuts({ signal: 'not_applicable', adsSignal: 'not_applicable', regime: identityOptOut ? 'first_party' : 'none' });
}

module.exports = {
  ADS_OPT_OUT_STORAGE_KEY,
  DEFAULT_TIMEOUT_MS,
  GPP_OPT_OUT_FIELDS,
  OPT_OUT_STORAGE_KEY,
  TCF_ADS_PURPOSES,
  gppSignal,
  hasConsentApi,
  readAdsOptOut,
  readConsentSignal,
  readFirstPartyOptOut,
  tcfSignal,
  waitForConsentApi,
  watchConsentApi,
  writeAdsOptOut,
  writeFirstPartyOptOut
};
